/**
 * URL-safe ID encryption/decryption.
 *
 * Obfuscates numeric database IDs in browser URLs so users cannot
 * enumerate or guess other users' IDs. Not meant to be
 * cryptographically strong — just an opaque, reversible encoding.
 *
 * Format:  "u_<base64url(salt + little-endian 4-byte id)>"
 * Example: encryptId(42) → "u_AABqAA"
 */

// Simple salt to add a layer of obfuscation (not a secret key)
const SALT = 0x54_4E_42_01; // "TNB" + version byte

function numberToBytes(n: number): Uint8Array {
  const buf = new ArrayBuffer(4);
  new DataView(buf).setUint32(0, n, true); // little-endian
  return new Uint8Array(buf);
}

function bytesToNumber(bytes: Uint8Array): number {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, true);
}

/** Encode a numeric ID into an opaque URL-safe string. */
export function encryptId(id: number | string): string {
  const num = typeof id === 'string' ? parseInt(id, 10) : id;
  if (!Number.isFinite(num) || num <= 0) return '';

  // XOR the ID with the salt for light obfuscation
  const masked = num ^ SALT;
  const idBytes = numberToBytes(masked);

  // Base64url encode (no padding)
  const base64 = btoa(String.fromCharCode(...idBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return `u_${base64}`;
}

/** Decode an opaque URL-safe string back to a numeric ID. Returns null on failure. */
export function decryptId(encrypted: string): number | null {
  if (!encrypted || !encrypted.startsWith('u_')) return null;

  try {
    const base64 = encrypted.slice(2)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Add back padding
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);

    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const masked = bytesToNumber(bytes);
    const id = masked ^ SALT;

    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}
